import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCostCenter } from './queryKeys';

type AccountingReferenceType = 'cost_center' | 'profit_center';

export type CreateCostCenterResponse = {
  id: UUID;
};

export type CreateCostCenterPayload = {
  name: string;
  type: AccountingReferenceType;
  companyId?: UUID;
  companySyncKey?: string;
  orgUnits?: UUID | UUID[];
  orgUnitsSyncKeys?: string[];
  syncKey?: string | null;
  isActive?: boolean;
  start?: string; // ISO date-time string
  end?: string; // ISO date-time string
};

type CreateCostCenterMutationOptions = UseMutationOptions<
  CreateCostCenterResponse,
  Error,
  CreateCostCenterPayload
>;

export type CreateCostCenterMutateAsyncFunction = UseMutateAsyncFunction<
  CreateCostCenterResponse,
  Error,
  CreateCostCenterPayload
>;

const defaultPayload: CreateCostCenterPayload = {
  name: '',
  type: 'cost_center',
};

/**
 * Creates a new cost center (aka accounting reference).
 *
 * @param {CreateCostCenterPayload} costCenterData - The data for creating the cost center
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Accounting-Reference/operation/create_accounting_reference_accounting_reference_post
 */
export const createCostCenter = async (
  costCenterData: CreateCostCenterPayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.COST_CENTER.CREATE(), {
      json: {
        ...defaultPayload,
        ...costCenterData,
      },
    })
    .json<CreateCostCenterResponse>();

  return response;
};

/**
 * Custom hook for creating a cost center.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationCreateCostCenter = (
  options?: CreateCostCenterMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (costCenterData: CreateCostCenterPayload) =>
      createCostCenter(costCenterData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysCostCenter.getAll({}),
      });
    },
    ...options,
  });
};
